import dayjs from "dayjs";

import { useFormatter } from "@/utils/useFormatter";

/** Fallback padrão para campos vazios em listas (paridade com PatientSearch). */
export const CRM_DISPLAY_EMPTY = "—";

/** Exibe nome com capitalização do legado (`formatNameDisplay`). */
export function formatPatientDisplayName(name: string | undefined): string {
  const trimmed = name?.trim();
  if (!trimmed) return CRM_DISPLAY_EMPTY;
  return useFormatter.formatNameDisplay(trimmed) || CRM_DISPLAY_EMPTY;
}

/** Nome principal do paciente (usual ou oficial). */
export function formatPatientPrimaryName(
  usualName: string | undefined,
  officialName: string | undefined,
): string {
  return formatPatientDisplayName(usualName ?? officialName);
}

/**
 * Data de nascimento ISO (`YYYY-MM-DD`) + idade, como em PatientSearch.
 */
export function formatPatientBirthDateDisplay(iso: string | undefined): string {
  const trimmed = iso?.trim();
  if (!trimmed) return CRM_DISPLAY_EMPTY;

  const formattedDate = useFormatter.formatDate(trimmed);
  if (!formattedDate) return CRM_DISPLAY_EMPTY;

  const age = useFormatter.formatAge(trimmed);
  if (age === undefined || Number.isNaN(age)) {
    return formattedDate;
  }

  return `${formattedDate} - ${age} anos`;
}

/** Telefone mascarado (BR) ou em dash se ausente. */
export function formatPatientPhoneDisplay(phone: string | undefined): string {
  const trimmed = phone?.trim();
  if (!trimmed) return CRM_DISPLAY_EMPTY;
  return useFormatter.formatPhoneNumber(trimmed) || CRM_DISPLAY_EMPTY;
}

/** CPF/identificador fiscal para exibição. */
export function formatPatientTaxIdentifierDisplay(
  value: string | undefined,
): string {
  const trimmed = value?.trim();
  if (!trimmed) return CRM_DISPLAY_EMPTY;
  return useFormatter.formatCPF(trimmed) || trimmed;
}

/** Data/hora de envio ou histórico (`DD/MM/YYYY HH:mm`). */
export function formatCommunicationsDateTimeDisplay(
  iso: string | undefined,
): string {
  const trimmed = iso?.trim();
  if (!trimmed) return CRM_DISPLAY_EMPTY;

  const parsed = dayjs(trimmed);
  if (!parsed.isValid()) return CRM_DISPLAY_EMPTY;

  return parsed.format("DD/MM/YYYY HH:mm");
}

/** Última consulta (`YYYY-MM-DD`) para colunas de atalho. */
export function formatLastAppointmentDisplay(iso: string | undefined): string {
  if (!iso?.trim()) return "Sem registro";

  const formatted = useFormatter.formatDate(iso);
  if (formatted) return formatted;

  const parsed = dayjs(iso);
  if (parsed.isValid()) return parsed.format("DD/MM/YYYY");

  return "Sem registro";
}

/** Ordenação decrescente por instante ISO (histórico, envios). */
export function compareCommunicationsDateTimeDesc(a: string, b: string): number {
  return dayjs(b).valueOf() - dayjs(a).valueOf();
}
